/**
 * onePagerModelV2.ts — expanded PDF model with full site/block/block-group/tract data.
 *
 * NOT YET WIRED INTO THE APP. This is a draft model for review before replacing V1.
 *
 * Key differences from V1:
 *   - Nearby competition & amenities moved up to site level (under site details)
 *   - Block scores AND their retail/dwell inputs always shown (both corridor and retail data)
 *   - Block group scores AND their HCC/HCO inputs always shown
 *   - ALL tract metric groups shown (not just the dominant one), dominant highlighted
 *   - L2 vs DCFC charger breakdown in competition section
 *   - Geography clearly labeled on every row
 */
import { SCORE_DEFS } from '../scoreDefinitions'
import type { GeoLevel } from '../geoLevel'
import {
  INPUT_GROUPS,
  formatValue,
  formatSubIndex,
  ordinalSuffix,
  dominantTypeToGroup,
} from '../siteAnalysis'
import { type CensusTract, type SiteScoreResult } from '../tractModel'
import {
  canonicalNearbyAmenities,
  type NearbyStats,
  type NearestStation,
  type CanonicalNearbyAmenities,
} from '../nearbyStats'
import {
  classificationPrimary,
  classificationSentences,
  formatClassScore,
  siteSpecificRows,
  type LocationClassification,
} from '../locationClassification'
import { stateLabel as fullStateName } from '../stateNames'

// ─── Input ───────────────────────────────────────────────────────────────────

/** Raw block-level properties from the vicinity API (new columns from migration 020). */
export interface BlockInputs {
  jobs_retail_pct?: number | null
  jobs_food_accommodation_pct?: number | null
  jobs_entertainment_pct?: number | null
  jobs_healthcare_pct?: number | null
  daytime_pop_ratio?: number | null
  population_blk?: number | null
  population_density_blk?: number | null
}

/** Raw block-group properties from the vicinity API (new columns from migration 019). */
export interface BlockGroupInputs {
  pct_renter_bg?: number | null
  multifamily_share_bg?: number | null
  mean_commute_time_bg?: number | null
  sf_detached_share_bg?: number | null
  pct_owner_occupied_bg?: number | null
  mean_vehicles_per_hh_bg?: number | null
  pct_drove_alone_bg?: number | null
  median_home_value_bg?: number | null
  population_bg?: number | null
  population_density_bg?: number | null
  household_density_bg?: number | null
}

/** Pre-computed percentile breakpoints (1st–99th) for station scores in a state.
 *  Each array has 99 values: index 0 = 1st percentile, index 98 = 99th percentile.
 *  To find a score's percentile, find the first breakpoint it exceeds. */
export interface StatePercentiles {
  stateLabel: string
  stationCount: number
  percentiles: number[] // [1, 2, ..., 99]
  site_lvi: number[]
  site_cdpi: number[]
  site_cmpi: number[]
  gap_score: number[]
  site_ppi: number[]
}

export interface OnePagerV2Input {
  tract: CensusTract
  address?: string | null
  addressApproximate?: boolean
  stateLabel: string
  dataVintage: string
  year: number
  siteScore?: SiteScoreResult | null
  nearby?: NearbyStats | null
  classification?: LocationClassification | null
  /** Raw block properties from the vicinity API — carries the retail/dwell inputs. */
  blockInputs?: BlockInputs | null
  /** Raw block group properties from the vicinity API — carries the HCC/HCO inputs. */
  blockGroupInputs?: BlockGroupInputs | null
  /** Pre-computed sorted score arrays for the state — used to compute percentile placement. */
  statePercentiles?: StatePercentiles | null
}

// ─── Output model ────────────────────────────────────────────────────────────

export interface LabeledRow {
  label: string
  value: string
  level: GeoLevel
}

export interface OnePagerV2Model {
  header: {
    address: string | null
    addressApproximate: boolean
    tractLabel: string
    stateName: string
    vintageLabel: string
  }

  hero: {
    score: number
    scoreColor: string
    label: string
    scope: 'site' | 'tract'
    description: string
    tractBaseline: { label: string; value: string } | null
    degradedNote: string | null
    /** Percentile among scored stations in the state, e.g. "72nd percentile of CA stations" */
    statePercentileNote: string | null
  }

  classification: {
    available: boolean
    badge: {
      label: string
      scoreLabel: string | null
      scoreText: string | null
      level: GeoLevel | null
    } | null
    sentences: string[]
    note?: string
  }

  subScores: Array<{
    label: string
    sublabel?: string | null
    value: string
    valueColor: string
    level: GeoLevel
    percentileNote: string | null
  }>

  /** Site-level details: D/F/M inputs + nearby competition + amenities */
  siteDetails: {
    available: boolean
    inputs: Array<{ label: string; value: string }> | null
    nearby: OnePagerV2Nearby
  }

  /** Block-level scores and inputs — null when no block data */
  blockData: {
    available: boolean
    scores: LabeledRow[]
    /** Retail/dwell inputs at block level (jobs mix, daytime pop ratio) */
    retailInputs: LabeledRow[]
    /** Context at block level */
    context: LabeledRow[]
  }

  /** Block group scores and inputs — null when no block group data */
  blockGroupData: {
    available: boolean
    scores: LabeledRow[]
    dominantType: string | null
    /** HCC inputs */
    hccInputs: LabeledRow[]
    /** HCO inputs */
    hcoInputs: LabeledRow[]
    /** Context at block group level */
    context: LabeledRow[]
  }

  /** ALL tract metric groups, with the dominant one marked */
  tractMetrics: Array<{
    key: string
    title: string
    isDominant: boolean
    rows: LabeledRow[]
  }>

  glossary: Array<{
    term: string
    definition: string
    range: string
    inputs?: string
  }>

  footer: {
    disclaimer: string
    methodologyUrl: string
  }

  meta: {
    usesThirdPartyBasemap: false
  }
}

// ─── Nearby (site-level) ─────────────────────────────────────────────────────

export type OnePagerV2Nearby =
  | { available: false; note: string }
  | {
      available: true
      columns: string[]
      competitionRows: Array<{ label: string; values: string[] }>
      amenities: {
        radiusMi: number
        rows: Array<{ label: string; value: string }>
        areFloor: boolean
      }
      lines: string[]
      footnotes: string[]
    }

// ─── Color banding ───────────────────────────────────────────────────────────

const SCORE_HEX = {
  high: '#15803d',
  good: '#4ade80',
  moderate: '#f59e0b',
  low: '#f97316',
  poor: '#ef4444',
} as const
const INK = '#1a1a1a'

function scoreHex(value: number): string {
  if (value >= 70) return SCORE_HEX.high
  if (value >= 60) return SCORE_HEX.good
  if (value >= 45) return SCORE_HEX.moderate
  if (value >= 35) return SCORE_HEX.low
  return SCORE_HEX.poor
}
const cmpiHex = (value: number): string => scoreHex(100 - value)

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DEFAULT_AMENITY_RADIUS_MI = 0.25
const NEARBY_POI_RESULT_CAP_FALLBACK = 100

const NEARBY_NO_PIN_NOTE =
  'Nearby competition and amenities are measured from a searched address. This profile was ' +
  'opened from the map, so no site point is available — search the address to include them.'

const miLabel = (mi: number): string => `${mi.toFixed(1)} mi`
const amenityMiLabel = (mi: number): string => `${Number(mi.toFixed(2))} mi`

function stationPhrase(s: NearestStation): string {
  const nacs = s.hasNacs ? ' · NACS/Tesla' : ''
  const name = s.name.trim() || 'unnamed station'
  return `${s.network} — ${name} (${s.distanceMi.toFixed(2)} mi · ${s.portsLevel2} L2 · ${s.portsDcFast} DCFC${nacs})`
}

function fmtOrDash(v: number | null | undefined, fmt: string): string {
  if (v == null || Number.isNaN(v)) return '—'
  return formatValue(v, fmt as Parameters<typeof formatValue>[1])
}

function pctOrDash(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `${(v * 100).toFixed(1)}%`
}

function numOrDash(v: number | null | undefined, decimals = 0): string {
  if (v == null || Number.isNaN(v)) return '—'
  return decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString()
}

function currencyOrDash(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `$${Math.round(v).toLocaleString()}`
}

/** Find the percentile of a value given breakpoints (1st–99th). */
function computePercentile(breakpoints: number[], value: number): number {
  // breakpoints[0] = 1st percentile, breakpoints[98] = 99th
  for (let i = breakpoints.length - 1; i >= 0; i--) {
    if (value >= breakpoints[i]!) return i + 1
  }
  return 0
}

function percentileNote(
  breakpoints: number[] | undefined,
  value: number | null,
  stateLabel: string,
  stationCount: number,
): string | null {
  if (!breakpoints || breakpoints.length === 0 || value == null) return null
  const pct = computePercentile(breakpoints, value)
  const ord = `${pct}${ordinalSuffix(pct)}`
  return `${ord} percentile of ${stationCount.toLocaleString()} ${stateLabel} stations`
}

function ordinal(p: number | null | undefined): string | null {
  if (p == null || Number.isNaN(p)) return null
  const r = Math.round(p)
  return `${r}${ordinalSuffix(r)}`
}

// ─── Nearby builder ──────────────────────────────────────────────────────────

function buildNearbyV2(
  stats: NearbyStats | null | undefined,
  canonical: CanonicalNearbyAmenities | null,
): OnePagerV2Nearby {
  if (!stats) return { available: false, note: NEARBY_NO_PIN_NOTE }

  const { rings } = stats
  const outer = rings[rings.length - 1]!
  const outerLabel = miLabel(outer.radiusMi)
  const columns = rings.map((r) => miLabel(r.radiusMi))
  const row = (label: string, pick: (r: (typeof rings)[number]) => number) => ({
    label,
    values: rings.map((r) => String(pick(r))),
  })

  const lines: string[] = []
  if (outer.stations === 0) {
    lines.push(`No charging stations within ${outerLabel} of this site.`)
  }
  if (stats.nearest) lines.push(`Nearest station: ${stationPhrase(stats.nearest)}`)
  if (stats.nearestAlt) {
    lines.push(`Nearest different-network competitor: ${stationPhrase(stats.nearestAlt)}`)
  }
  if (outer.topNetwork) {
    lines.push(
      `Largest network within ${outerLabel}: ${outer.topNetwork} ` +
        `(${Math.round(outer.topNetworkPortShare * 100)}% of ports).`,
    )
  }
  if (outer.stations > 0) {
    const pct = Math.round((outer.nacsStations / outer.stations) * 100)
    lines.push(
      `NACS/Tesla connector availability: ${outer.nacsStations} of ${outer.stations} stations ` +
        `(${pct}%) within ${outerLabel}.`,
    )
  }
  if (stats.brands.length > 0) {
    const run = stats.brands.map((b) => `${b.brand} (${b.count})`).join(', ')
    lines.push(`Notable brands within ${outerLabel}: ${run}`)
  }

  const amenityFloor = canonical?.areFloor ?? false
  const fmtAmenity = (n: number): string => (amenityFloor ? `${n}+` : String(n))
  const amenities = {
    radiusMi: canonical?.radiusMi ?? DEFAULT_AMENITY_RADIUS_MI,
    rows: (canonical?.rows ?? []).map((r) => ({ label: r.label, value: fmtAmenity(r.count) })),
    areFloor: amenityFloor,
  }

  const footnotes: string[] = []
  if (canonical && amenityFloor) {
    footnotes.push(
      `Amenity counts marked "+" are floors — AT LEAST the number shown: within ` +
        `${amenityMiLabel(canonical.radiusMi)} the place search stops at its nearest ` +
        `${canonical.poiResultCap}, so a dense area can hold several times as many.`,
    )
  }
  footnotes.push(
    'L2-only stations have no DC fast ports. DC fast stations may also have L2 ports. ' +
      'Together they equal the total station count.',
  )
  if (stats.stationsAreFloor) {
    footnotes.push(
      'Station counts are AT LEAST the numbers shown: the charging-station lookup returned its ' +
        'maximum number of results within these radii.',
    )
  }

  return {
    available: true,
    columns,
    competitionRows: [
      row('Stations', (r) => r.stations),
      row('L2-only stations', (r) => r.stations - r.dcFastStations),
      row('DC fast stations', (r) => r.dcFastStations),
      row('L2 ports', (r) => r.l2Ports),
      row('DC fast ports', (r) => r.dcFastPorts),
      row('Total ports', (r) => r.totalPorts),
      row('Stations w/ NACS', (r) => r.nacsStations),
      row('Networks', (r) => r.networks),
    ],
    amenities,
    lines,
    footnotes,
  }
}

// ─── Block data builder ──────────────────────────────────────────────────────

function buildBlockData(
  classification: LocationClassification | null | undefined,
  inputs?: BlockInputs | null,
): {
  available: boolean
  scores: LabeledRow[]
  retailInputs: LabeledRow[]
  context: LabeledRow[]
} {
  if (!classification?.block) {
    return { available: false, scores: [], retailInputs: [], context: [] }
  }

  const b = classification.block
  const scores: LabeledRow[] = []
  const bs = b.scores

  // Block scores are stored as [0,1]; display as 0-100 for consistency
  const to100 = (v: number | null): string => (v == null ? '—' : (v * 100).toFixed(1))

  if (bs.corridor != null)
    scores.push({ label: 'Corridor Score', value: to100(bs.corridor), level: 'block' })
  if (bs.retailDestination != null)
    scores.push({
      label: 'Retail Destination Score',
      value: to100(bs.retailDestination),
      level: 'block',
    })
  if (bs.dwellTime != null)
    scores.push({ label: 'Dwell Time Score', value: to100(bs.dwellTime), level: 'block' })

  const retailInputs: LabeledRow[] = [
    { label: 'Retail job share', value: pctOrDash(inputs?.jobs_retail_pct), level: 'block' },
    {
      label: 'Food & accommodation job share',
      value: pctOrDash(inputs?.jobs_food_accommodation_pct),
      level: 'block',
    },
    {
      label: 'Entertainment job share',
      value: pctOrDash(inputs?.jobs_entertainment_pct),
      level: 'block',
    },
    {
      label: 'Healthcare job share',
      value: pctOrDash(inputs?.jobs_healthcare_pct),
      level: 'block',
    },
    {
      label: 'Daytime population ratio',
      value: numOrDash(inputs?.daytime_pop_ratio, 2),
      level: 'block',
    },
  ]

  const context: LabeledRow[] = [
    { label: 'Population', value: numOrDash(inputs?.population_blk), level: 'block' },
    {
      label: 'Population density',
      value: numOrDash(inputs?.population_density_blk),
      level: 'block',
    },
  ]

  return { available: true, scores, retailInputs, context }
}

// ─── Block group data builder ────────────────────────────────────────────────

function buildBlockGroupData(
  classification: LocationClassification | null | undefined,
  inputs?: BlockGroupInputs | null,
): {
  available: boolean
  scores: LabeledRow[]
  dominantType: string | null
  hccInputs: LabeledRow[]
  hcoInputs: LabeledRow[]
  context: LabeledRow[]
} {
  if (!classification?.blockGroup) {
    return {
      available: false,
      scores: [],
      dominantType: null,
      hccInputs: [],
      hcoInputs: [],
      context: [],
    }
  }

  const bg = classification.blockGroup
  const scores: LabeledRow[] = []
  const bgs = bg.scores

  // Block group scores are stored as [0,1]; display as 0-100 for consistency
  const to100 = (v: number | null): string => (v == null ? '—' : (v * 100).toFixed(1))

  if (bgs.homeChargingConstrained != null)
    scores.push({
      label: 'Home-Charging Constrained',
      value: to100(bgs.homeChargingConstrained),
      level: 'block-group',
    })
  if (bgs.homeChargingOptimized != null)
    scores.push({
      label: 'Home-Charging Optimized',
      value: to100(bgs.homeChargingOptimized),
      level: 'block-group',
    })

  const hccInputs: LabeledRow[] = [
    { label: 'Renter share', value: pctOrDash(inputs?.pct_renter_bg), level: 'block-group' },
    {
      label: 'Multifamily share',
      value: pctOrDash(inputs?.multifamily_share_bg),
      level: 'block-group',
    },
    {
      label: 'Mean commute time (min)',
      value: numOrDash(inputs?.mean_commute_time_bg, 1),
      level: 'block-group',
    },
  ]

  const hcoInputs: LabeledRow[] = [
    {
      label: 'SF detached share',
      value: pctOrDash(inputs?.sf_detached_share_bg),
      level: 'block-group',
    },
    {
      label: 'Owner-occupied share',
      value: pctOrDash(inputs?.pct_owner_occupied_bg),
      level: 'block-group',
    },
    {
      label: 'Mean vehicles per HH',
      value: numOrDash(inputs?.mean_vehicles_per_hh_bg, 2),
      level: 'block-group',
    },
    { label: '% drove alone', value: pctOrDash(inputs?.pct_drove_alone_bg), level: 'block-group' },
    {
      label: 'Median home value',
      value: currencyOrDash(inputs?.median_home_value_bg),
      level: 'block-group',
    },
  ]

  const context: LabeledRow[] = [
    { label: 'Population', value: numOrDash(inputs?.population_bg), level: 'block-group' },
    {
      label: 'Population density',
      value: numOrDash(inputs?.population_density_bg),
      level: 'block-group',
    },
    {
      label: 'Household density',
      value: numOrDash(inputs?.household_density_bg),
      level: 'block-group',
    },
  ]

  const dominantType = bg.label ?? null

  return { available: true, scores, dominantType, hccInputs, hcoInputs, context }
}

// ─── Tract metrics builder (all groups, dominant marked) ─────────────────────

function buildTractMetrics(
  tract: CensusTract,
  dominantKey: string,
): Array<{
  key: string
  title: string
  isDominant: boolean
  rows: LabeledRow[]
}> {
  return INPUT_GROUPS.map((group) => ({
    key: group.key,
    title: group.title,
    isDominant: group.key === dominantKey,
    rows: group.variables.map((v) => ({
      label: v.label,
      value: formatValue(
        (tract as unknown as Record<string, number | null>)[v.tractField] ?? null,
        v.format as Parameters<typeof formatValue>[1],
      ),
      level: v.level,
    })),
  }))
}

// ─── Main builder ────────────────────────────────────────────────────────────

export function buildOnePagerModelV2(input: OnePagerV2Input): OnePagerV2Model {
  const {
    tract,
    address = null,
    addressApproximate = false,
    stateLabel,
    dataVintage,
    year,
    nearby = null,
    siteScore = null,
    classification = null,
    blockInputs = null,
    blockGroupInputs = null,
    statePercentiles = null,
  } = input

  const dominantKey = dominantTypeToGroup(tract.dominantType) ?? INPUT_GROUPS[0]!.key

  // Percentile helper scoped to this state's data
  const pctNote = (arr: number[] | undefined, value: number | null) =>
    statePercentiles
      ? percentileNote(arr, value, statePercentiles.stateLabel, statePercentiles.stationCount)
      : null

  // Canonical amenities
  const amenityData = siteScore?.amenities
  const canonicalAmenities: CanonicalNearbyAmenities | null =
    amenityData?.nearbyPois && amenityData.nearbyPois.length > 0
      ? canonicalNearbyAmenities(amenityData.nearbyPois, {
          radiusMi: amenityData.poiRadiusMi ?? DEFAULT_AMENITY_RADIUS_MI,
          poiFetched: amenityData.poiFetched ?? 0,
          poiResultCap: amenityData.poiResultCap ?? NEARBY_POI_RESULT_CAP_FALLBACK,
        })
      : null

  // Site score vs tract score
  const degraded = siteScore?.v2?.degraded ?? false
  const hasSiteHeadline = siteScore != null && !degraded
  const heroScore = siteScore ? siteScore.opportunityScore : tract.lviScore
  const v2 = hasSiteHeadline ? siteScore?.v2 : null

  // Sub-scores: use site-adjusted when available
  const ppi = v2?.sitePpi ?? tract.ppiScore
  const cdpi = v2?.siteCdpi ?? tract.cdpiScore
  const cmpi = v2?.siteCmpi ?? tract.cmpiScore
  const gap = v2?.gapScore ?? tract.gapScore

  // Classification
  const classificationData = (() => {
    if (!classification) {
      return {
        available: false as const,
        badge: null,
        sentences: [] as string[],
        note: 'Unavailable for this export — search an address to include it.',
      }
    }
    const primary = classificationPrimary(classification)
    const s = classificationSentences(classification)
    return {
      available: true as const,
      badge: primary
        ? {
            label: primary.label,
            scoreLabel: primary.scoreLabel,
            scoreText: primary.scoreText,
            level:
              primary.scoreText === null
                ? null
                : primary.source === 'block'
                  ? ('block' as GeoLevel)
                  : ('block-group' as GeoLevel),
          }
        : null,
      sentences: [s.block, s.blockGroup],
    }
  })()

  return {
    header: {
      address: address ?? null,
      addressApproximate: address ? addressApproximate : false,
      tractLabel: `Census Tract ${tract.tractId}`,
      stateName: fullStateName(stateLabel),
      vintageLabel: `Site profile · data as of ${dataVintage}`,
    },

    hero: {
      score: heroScore,
      scoreColor: scoreHex(heroScore),
      label: hasSiteHeadline ? `Site ${SCORE_DEFS.LVI.shortLabel}` : SCORE_DEFS.LVI.shortLabel,
      scope: hasSiteHeadline ? 'site' : 'tract',
      description: SCORE_DEFS.LVI.shortDesc,
      tractBaseline: hasSiteHeadline
        ? { label: 'Census tract score', value: siteScore!.lviBaseline.toFixed(1) }
        : null,
      degradedNote:
        siteScore != null && degraded
          ? "Site-specific score pending data — showing this location's tract-level score."
          : null,
      statePercentileNote: pctNote(statePercentiles?.site_lvi, heroScore),
    },

    classification: classificationData,

    subScores: [
      {
        label: SCORE_DEFS.CDPI.shortLabel,
        value: formatSubIndex(cdpi),
        valueColor: scoreHex(cdpi),
        level: hasSiteHeadline ? 'site' : 'tract',
        percentileNote: pctNote(statePercentiles?.site_cdpi, cdpi),
      },
      {
        label: SCORE_DEFS.CMPI.shortLabel,
        sublabel: '(lower is better)',
        value: formatSubIndex(cmpi),
        valueColor: cmpiHex(cmpi),
        level: hasSiteHeadline ? 'site' : 'tract',
        percentileNote: pctNote(statePercentiles?.site_cmpi, cmpi),
      },
      {
        label: SCORE_DEFS.Gap.shortLabel,
        value: formatSubIndex(gap),
        valueColor: INK,
        level: hasSiteHeadline ? 'site' : 'tract',
        percentileNote: pctNote(statePercentiles?.gap_score, gap),
      },
      {
        label: SCORE_DEFS.PPI.shortLabel,
        value: formatSubIndex(ppi),
        valueColor: scoreHex(ppi),
        level: 'tract',
        percentileNote: pctNote(statePercentiles?.site_ppi, ppi),
      },
    ],

    siteDetails: {
      available: siteScore?.v2 != null,
      inputs: siteScore?.v2
        ? [
            {
              label: 'Distance to nearest ramp',
              value:
                siteScore.v2.rampDistanceMi != null
                  ? `${siteScore.v2.rampDistanceMi.toFixed(2)} mi`
                  : '—',
            },
            {
              label: `POIs within ${siteScore.amenities?.poiRadiusMi ?? DEFAULT_AMENITY_RADIUS_MI} mi`,
              value: siteScore.v2.poiCount?.toString() ?? '—',
            },
            {
              label: `Competing stations within ${siteScore.v2.competition?.radiusMi ?? 0.5} mi`,
              value: siteScore.evCompetition ? `${siteScore.evCompetition.stationCount}` : '—',
            },
            {
              label: 'Distinct networks',
              value: siteScore.v2.competition?.distinctNetworks?.toString() ?? '—',
            },
          ]
        : null,
      nearby: buildNearbyV2(nearby, canonicalAmenities),
    },

    blockData: buildBlockData(classification, blockInputs),

    blockGroupData: buildBlockGroupData(classification, blockGroupInputs),

    tractMetrics: buildTractMetrics(tract, dominantKey),

    glossary: [
      {
        term: 'Opportunity Score (LVI)',
        definition:
          'Overall investment opportunity for new EV charging at this site — blends the demand-vs-supply gap, pricing power, and site accessibility into one ranking.',
        range: '0–100',
        inputs:
          'Gap Score, Pricing Power Index, Site Accessibility. Weights vary by location type.',
      },
      {
        term: 'Demand Potential Index (CDPI)',
        definition:
          "Latent unmet demand potential for EV charging — combining corridor traffic, daytime destinations, and residents who can't charge at home.",
        range: '0–100',
        inputs: 'Adjusted at the site level by distance to nearest ramp and nearby POI count.',
      },
      {
        term: 'Market Pressure Index (CMPI)',
        definition:
          'How saturated the existing charging market is. A score of 0 means no existing stations nearby.',
        range: '0–100',
        inputs:
          'Adjusted at the site level by weighted competition within 0.5 mi: DCFC = 1.0, L2 = 0.4 per station.',
      },
      {
        term: 'Gap Score',
        definition:
          'Demand Potential minus Market Pressure. Positive = demand exceeds supply (opportunity). Negative = oversaturated.',
        range: '−100 to +100',
      },
      {
        term: 'Pricing Power Index (PPI)',
        definition:
          'How much premium pricing the local market can support — driven by income, EV adoption, and limited competition.',
        range: '0–100',
      },
      {
        term: 'Corridor Score (CS)',
        definition:
          'How well-positioned the block is along high-traffic EV travel corridors (interstates, ramps, AFC corridors).',
        range: '0–100',
        inputs:
          'Distance to interstate, proximity to ramps/rest areas, AFC corridor designation, high-volume road AADT.',
      },
      {
        term: 'Retail Destination Score (RDS)',
        definition:
          'Attractiveness as a retail/commercial destination where EV drivers would stop and charge.',
        range: '0–100',
        inputs:
          'Retail+food+entertainment job share, Dwell Time Score, daytime population ratio, commercial proxy. Normalized within RUCA class.',
      },
      {
        term: 'Dwell Time Score (DTS)',
        definition:
          'How long visitors typically linger, based on local job mix. Higher = better fit for charging (especially L2).',
        range: '0–100',
        inputs:
          'Retail jobs, food & accommodation, entertainment, healthcare. Normalized within RUCA class.',
      },
      {
        term: 'Home-Charging Constrained (HCC)',
        definition:
          "Share of residents who can't easily charge at home — renters, multifamily housing. High HCC = more reliance on public charging.",
        range: '0–100',
        inputs:
          'Renter share, multifamily housing share, commute time. Measured at block group level.',
      },
      {
        term: 'Home-Charging Optimized (HCO)',
        definition:
          'Share of residents who can charge at home — single-family, owner-occupied, vehicle-rich neighborhoods.',
        range: '0–100',
        inputs:
          'SF detached share, owner-occupied share, vehicles per household, % drove alone, median home value. Measured at block group level.',
      },
      {
        term: 'Site Accessibility (SAM)',
        definition:
          'How physically accessible and commercially viable the location is for new chargers. Modulates the Opportunity Score.',
        range: '0–100',
        inputs: 'Adjusted by residential context: block group housing type and density.',
      },
    ],

    footer: {
      disclaimer:
        `OptiYield Opportunity Score and sub-indices are a decision-support signal derived ` +
        `from public census, EV-registration, and charging-infrastructure data as of ${dataVintage}. ` +
        `They are not investment, financial, or real-estate advice. Verify site-specific factors ` +
        `independently before acting. \u00A9 ${year} OptiYield.`,
      methodologyUrl: 'optiyield.app/methodology',
    },

    meta: { usesThirdPartyBasemap: false },
  }
}
